import { useTranslation } from "react-i18next";
import type { LunchMenuItem } from "../../../../api/lunchMenus";
import { fmtEuro } from "../../../../utils/currency";
import { useViewport } from "../../../../hooks/useViewport";
import { lunchUnitOf, useLunchProducts } from "../useLunchProducts";

/** Порции на ден: „20/ден" или „∞" (0 = без ограничение). */
const DailyLimitCell = ({ dailyLimit }: { dailyLimit: number }) => {
  const { t } = useTranslation();
  return dailyLimit > 0 ? (
    <span className="font-semibold text-pos-accent-ink">
      {t("lunchMenu.dailyLimitShort", { count: dailyLimit })}
    </span>
  ) : (
    // Знакът ∞ е дребен в mono шрифта на колоната — обикновеният шрифт и 17px,
    // същото като в редактора (LunchSelectedRow)
    <span title={t("lunchMenu.dailyLimitUnlimited")} className="font-pos-sans text-[17px] leading-none">
      ∞
    </span>
  );
};

/** Основната цена се зачертава САМО ако обедната е различна от нея. */
const priceChangedOf = (mainPrice: number | null, lunchPrice: string) =>
  mainPrice != null && Math.abs(Number(lunchPrice) - mainPrice) >= 0.005;

/** Размерът: при сменен — ~~стар~~ нов (като цената), иначе на продукта. */
const UnitCell = ({
  mainUnit,
  unit,
}: {
  mainUnit: string | null;
  unit: { text: string | null; changed: boolean };
}) =>
  unit.changed ? (
    <>
      {mainUnit && (
        <span className="text-pos-muted line-through">{mainUnit}</span>
      )}
      {mainUnit && " "}
      <span className="font-semibold text-pos-accent-ink">{unit.text}</span>
    </>
  ) : (
    <>{unit.text ?? "—"}</>
  );

/** Продуктите на обедно меню: размер · основна (зачертана) · обедна цена. */
export const LunchMenuItemsTable = ({ items }: { items: LunchMenuItem[] }) => {
  const { t, i18n } = useTranslation();
  const productOf = useLunchProducts();
  const { isNarrow } = useViewport();
  const headCell =
    "font-pos-mono text-[11px] font-semibold uppercase tracking-[.08em] text-pos-dim";

  // Телефон: име + „размер · ~~основна~~" на втори ред и обедната цена вдясно
  if (isNarrow)
    return (
      <div className="overflow-hidden rounded-[14px] border border-pos-line3 bg-pos-card">
        {items.map((lunchItem) => {
          const product = productOf(lunchItem.itemId);
          const unit = lunchUnitOf(
            lunchItem.unitAndSizeLangs,
            i18n.language,
            product.mainUnit,
          );
          return (
            <div
              key={lunchItem.itemId}
              className="flex items-center gap-2.5 border-b border-pos-rowline px-3 py-2 last:border-b-transparent"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[13.5px] font-semibold text-pos-ink">
                  {product.name}
                </span>
                <span className="font-pos-mono text-[11px] text-pos-soft2">
                  <UnitCell mainUnit={product.mainUnit} unit={unit} />
                  {" · "}
                  <DailyLimitCell dailyLimit={lunchItem.dailyLimit} />
                  {priceChangedOf(product.mainPrice, lunchItem.price) && (
                    <>
                      {" · "}
                      <span className="line-through">
                        {fmtEuro(product.mainPrice)}
                      </span>
                    </>
                  )}
                </span>
              </div>
              <span className="font-pos-mono text-[15px] font-bold text-pos-ink">
                {fmtEuro(lunchItem.price)}
              </span>
            </div>
          );
        })}
      </div>
    );

  return (
    <div className="overflow-hidden rounded-[14px] border border-pos-line3 bg-pos-card">
      <div className="flex min-h-9 items-center gap-3 border-b border-pos-rowline bg-pos-panel px-3.5">
        <span className={`${headCell} flex-1`}>
          {t("lunchMenu.colProduct")}
        </span>
        <span className={`${headCell} w-[150px]`}>
          {t("lunchMenu.colSize")}
        </span>
        <span className={`${headCell} w-[96px] text-right`}>
          {t("lunchMenu.colPortions")}
        </span>
        <span className={`${headCell} w-[92px] text-right`}>
          {t("lunchMenu.colMain")}
        </span>
        <span className={`${headCell} w-[100px] text-right`}>
          {t("lunchMenu.colLunch")}
        </span>
      </div>
      {items.map((lunchItem) => {
        const product = productOf(lunchItem.itemId);
        const unit = lunchUnitOf(
          lunchItem.unitAndSizeLangs,
          i18n.language,
          product.mainUnit,
        );
        return (
          <div
            key={lunchItem.itemId}
            className="flex min-h-[46px] items-center gap-3 border-b border-pos-rowline px-3.5 last:border-b-transparent"
          >
            <span
              className={`min-w-0 flex-1 truncate text-[15px] font-semibold ${
                product.item ? "text-pos-ink" : "italic text-pos-muted"
              }`}
            >
              {product.name}
            </span>
            <span className="w-[150px] truncate font-pos-mono text-[13px] text-pos-soft2">
              <UnitCell mainUnit={product.mainUnit} unit={unit} />
            </span>
            <span className="w-[96px] text-right font-pos-mono text-[13px] text-pos-soft2">
              <DailyLimitCell dailyLimit={lunchItem.dailyLimit} />
            </span>
            <span className="w-[92px] text-right font-pos-mono text-[13px] text-pos-muted">
              {product.mainPrice == null ? (
                "—"
              ) : priceChangedOf(product.mainPrice, lunchItem.price) ? (
                <span className="line-through">
                  {fmtEuro(product.mainPrice)}
                </span>
              ) : (
                fmtEuro(product.mainPrice)
              )}
            </span>
            <span className="w-[100px] text-right font-pos-mono text-base font-bold text-pos-ink">
              {fmtEuro(lunchItem.price)}
            </span>
          </div>
        );
      })}
    </div>
  );
};
